// =============================================================================
// BP-05-03: Calendar Frontend - CalendarPage
// =============================================================================

import React, { useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { SlotInfo } from 'react-big-calendar';
import { Plus, Settings } from 'lucide-react';
import {
  CalendarView,
  CalendarSidebar,
  AppointmentForm,
  CalendarExportModal,
} from '../components';
import {
  useCalendarRange,
  useCalendarEvents,
  useCalendarStore,
} from '../hooks';
import { type CalendarEvent, AppointmentStatus } from '../types';
import { cn } from '@/core/lib/utils';

// -----------------------------------------------------------------------------
// Mock Data (Replace with actual data fetching)
// -----------------------------------------------------------------------------

const mockChildren = [
  { id: '1', name: 'Emma' },
  { id: '2', name: 'Lucas' },
];

const mockCareTeam = [
  { id: '1', name: 'Dr. Sarah Chen', type: 'Neurologist' },
  { id: '2', name: 'Dr. Michael Park', type: 'Physical Therapist' },
  { id: '3', name: 'Lisa Johnson', type: 'Occupational Therapist' },
];

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

export function CalendarPage() {
  const { familyId = '' } = useParams<{ familyId: string }>();
  const navigate = useNavigate();

  const {
    currentDate,
    currentView,
    isSidebarOpen,
    isFormOpen,
    editingAppointmentId,
    selectedChildId,
    selectedAppointmentTypes,
    selectedStatuses,
    showDeclinedEvents,
    getDateRange,
    openForm,
    closeForm,
    setQuickAddSlot,
    openDetail,
    toggleSidebar,
  } = useCalendarStore();

  const [showExportModal, setShowExportModal] = useState(false);

  // Get date range for current view
  const dateRange = useMemo(() => getDateRange(), [getDateRange, currentDate, currentView]);

  // Fetch appointments
  const { data: appointments, isLoading } = useCalendarRange(
    familyId,
    {
      start: dateRange.start,
      end: dateRange.end,
      childId: selectedChildId || undefined,
      appointmentType: selectedAppointmentTypes.length === 1 ? selectedAppointmentTypes[0] : undefined,
    },
    { enabled: !!familyId }
  );

  // Filter appointments
  const filteredAppointments = useMemo(() => {
    if (!appointments) return [];

    return appointments.filter((apt) => {
      // Filter by appointment types
      if (
        selectedAppointmentTypes.length > 0 &&
        !selectedAppointmentTypes.includes(apt.appointmentType)
      ) {
        return false;
      }

      // Filter by status
      if (selectedStatuses.length > 0 && !selectedStatuses.includes(apt.status)) {
        return false;
      }

      // Filter cancelled/declined if not showing them
      if (!showDeclinedEvents && apt.status === AppointmentStatus.CANCELLED) {
        return false;
      }

      return true;
    });
  }, [appointments, selectedAppointmentTypes, selectedStatuses, showDeclinedEvents]);

  // Convert to calendar events
  const events = useCalendarEvents(filteredAppointments);

  // Handle event click
  const handleEventClick = useCallback(
    (event: CalendarEvent) => {
      navigate(`/family/${familyId}/appointments/${event.id}`);
    },
    [navigate, familyId]
  );

  // Handle slot selection (for creating new appointments)
  const handleSlotSelect = useCallback(
    (slotInfo: SlotInfo) => {
      setQuickAddSlot({
        start: slotInfo.start,
        end: slotInfo.end,
      });
    },
    [setQuickAddSlot]
  );

  // Handle appointment creation success
  const handleAppointmentSuccess = useCallback(() => {
    closeForm();
  }, [closeForm]);

  return (
    <div className="min-h-screen bg-cream">
      {/* Header */}
      <header className="bg-white border-b border-warm-gray/10 sticky top-0 z-40">
        <div className="max-w-[1800px] mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-display font-semibold text-navy">
                Calendar
              </h1>
              <p className="text-sm text-warm-gray mt-0.5">
                Manage appointments and schedules
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => toggleSidebar()}
                className="p-2 hover:bg-cream rounded-lg transition-colors lg:hidden"
              >
                <Settings className="w-5 h-5 text-navy" />
              </button>
              <button
                onClick={() => openForm()}
                className="flex items-center gap-2 px-4 py-2.5 bg-sage text-white rounded-lg font-medium hover:bg-sage-dark transition-colors"
              >
                <Plus className="w-5 h-5" />
                New Appointment
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-[1800px] mx-auto px-6 py-6">
        <div className="flex gap-6">
          {/* Sidebar */}
          <aside
            className={cn(
              'w-72 flex-shrink-0 transition-all duration-300',
              'hidden lg:block',
              !isSidebarOpen && 'lg:hidden'
            )}
          >
            <CalendarSidebar
              children={mockChildren}
              onCreateAppointment={() => openForm()}
              onExport={() => setShowExportModal(true)}
            />
          </aside>

          {/* Calendar */}
          <div className="flex-1 min-w-0">
            <CalendarView
              events={events}
              isLoading={isLoading}
              onEventClick={handleEventClick}
              onSlotSelect={handleSlotSelect}
            />
          </div>
        </div>
      </main>

      {/* Appointment Form Modal */}
      {isFormOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-navy/50 backdrop-blur-sm"
            onClick={closeForm}
          />
          <div className="relative bg-white rounded-2xl shadow-lifted w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <AppointmentForm
                familyId={familyId}
                children={mockChildren}
                careTeam={mockCareTeam}
                onSuccess={handleAppointmentSuccess}
                onCancel={closeForm}
              />
            </div>
          </div>
        </div>
      )}

      {/* Export Modal */}
      <CalendarExportModal
        familyId={familyId}
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        children={mockChildren}
      />
    </div>
  );
}

export default CalendarPage;
